import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { GetAdminFeedbackFileAPI } from '../../../../api/AdminAPI';

function isImageFile(file) {
  if (file.file_type && String(file.file_type).startsWith('image/')) return true;
  return /\.(png|jpe?g|gif|webp|svg)$/i.test(file.file_name || '');
}

/**
 * Salva um Blob com o nome informado (mesmo padrão do download de backups).
 * @param {Blob} blob
 * @param {string} name
 */
function saveBlob(blob, name) {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = name || 'anexo';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}

/**
 * FeedbackFileAttachment - Um anexo de feedback no diálogo de detalhes (Admin Geral)
 *
 * O anexo não tem URL pública: é baixado pelo endpoint autenticado (que
 * responde com o arquivo ou com 302 para o bucket) e exibido por blob URL.
 * Imagem: miniatura carregada ao abrir, clique abre em nova aba.
 * Outros tipos: chip que baixa o arquivo ao clicar.
 *
 * @param {Object} props
 * @param {{id: number, file_name: string, file_type?: string}} props.file
 */
function FeedbackFileAttachment({ file }) {
  const isImage = isImageFile(file);
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isImage) return undefined;
    let active = true;
    let objectUrl = null;
    setLoading(true);
    setError('');
    GetAdminFeedbackFileAPI(file.id)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        if (!active) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setImageUrl(objectUrl);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        setError(err.message || 'Erro ao abrir o anexo.');
        setLoading(false);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file.id, isImage]);

  const handleDownload = () => {
    setLoading(true);
    setError('');
    GetAdminFeedbackFileAPI(file.id)
      .then((blob) => {
        saveBlob(blob, file.file_name);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Erro ao abrir o anexo.');
        setLoading(false);
      });
  };

  if (isImage) {
    return (
      <Box>
        {imageUrl ? (
          <Link href={imageUrl} target="_blank" rel="noopener noreferrer">
            <Box
              component="img"
              src={imageUrl}
              alt={file.file_name}
              sx={{ maxWidth: '100%', maxHeight: 220, borderRadius: '8px', border: '1px solid #E8ECF0' }}
            />
          </Link>
        ) : loading ? (
          <CircularProgress size={20} />
        ) : null}
        <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ display: 'block', mt: 0.5 }}>
          {error || file.file_name}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Chip
        clickable
        disabled={loading}
        onClick={handleDownload}
        icon={loading
          ? <CircularProgress size={14} />
          : <InsertDriveFileOutlinedIcon sx={{ fontSize: '16px !important' }} />}
        label={file.file_name}
        deleteIcon={<FileDownloadOutlinedIcon sx={{ fontSize: '14px !important' }} />}
        onDelete={handleDownload}
        sx={{ justifyContent: 'flex-start', width: 'fit-content' }}
      />
      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

export default FeedbackFileAttachment;
